
import { Component, Prop, Vue } from 'vue-property-decorator';
import { initClient } from '../../../api';
import {
  ApprovalState,
  Group,
  GroupSurveyExtension,
  GroupSurveyExtensionApproval,
  GroupSurveyExtensionUiClient,
  GroupSurveyMapping,
  IGroup,
  IGroupSurveyExtension,
  IGroupSurveyExtensionApproval,
  IGroupSurveyMapping,
  SettingsApiControllerUIClient,
} from '../../backend/backendapi';
import { emptyUUID, formatDateTime, WindowType } from '../../../util';
import Toasted from 'vue-toasted';
import GroupEditor from '../../group/EditModal/GroupEditor';
import TabSlot from '../../group/EditModal/TabSlot';
import GroupSurveyEditor from '../../survey/group/EditModal/GroupSurveyEditor';
import GeneralAfter from '../../survey/group/EditModal/GeneralAfter';
import Spinner from '../../util/Spinner';
import CenterOnParent from '../../util/CenterOnParent';
import GroupSurveyGroupSelector from './AdvanceRegistration/GroupSurveyGroupSelector.vue';
import ApprovalGroupSelectCheckBoxes from './AdvanceRegistration/ApprovalGroupSelectCheckBoxes.vue';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    ApprovalGroupSelectCheckBoxes,
    GeneralAfter,
    GroupEditor,
    GroupSurveyEditor,
    GroupSurveyGroupSelector,
    Spinner,
    TabSlot,
    CenterOnParent
  },
})
export default class AdvanceRegistration extends Vue {
  @Prop({ default: null })
  groupSurveyExtensionId!: string | null;

  private readonly api = initClient(GroupSurveyExtensionUiClient);
  private readonly settingsApi = initClient(SettingsApiControllerUIClient);

  canToggleEnableScanEntry = false;
  extraInfoJson: string = '[]';
  groupSurveyExtension: GroupSurveyExtension | null = null;
  needsApproval = false;
  surveySelectionViaGroups = false;
  done = false;
  saving = false;
  imageUrl: string | null = null;

  GroupSurveyExtension = GroupSurveyExtension;
  formatDateTime = formatDateTime;

  // Add state for change of selection of survey groups, so we can compute if at least one is selected
  selectedSurveyGroups: GroupSurveyExtension[] = [];

  get isSurveySelectionGroupsSelectedOrInactive(): boolean {
    if (!this.surveySelectionViaGroups) return true;
    return this.selectedSurveyGroups.length > 0;
  }

  get canSave(): boolean {
    if (!this.groupSurveyExtension?.group.name
        || !this.groupSurveyExtension.scanEntryStartDate
        || !this.groupSurveyExtension.scanEntryEndDate
        || !this.groupSurveyExtension.responsibleContactId
        || !this.groupSurveyExtension.group.externalUsers.length
        || !this.groupSurveyExtension.approvals.every(approval => approval.approvalGroupId)) {
      return false;
    }
    if (this.needsApproval) {
      if (!this.groupSurveyExtension?.approvals.length) { //at least 1 approval group
        return false;
      }
    }
    return this.isSurveySelectionGroupsSelectedOrInactive;
  }

async mounted(): Promise<void> {
    await this.reloadSettingsAndInit();
  }

  async reloadSettingsAndInit(): Promise<void> {
    await this.reloadSetting();
    this.initNewRegistration();
  }

  initNewRegistration(): void {
    const startDate = new Date();
    // Round up to the next full hour (e.g., 14:15 becomes 15:00)
    startDate.setHours(startDate.getHours() + 1, 0, 0, 0);

    // Set end date to 1 hour after the new start date (e.g., 16:00)
    const endDate = new Date(startDate.getTime() + 60 * 60 * 1000);

    this.groupSurveyExtension = GroupSurveyExtension.fromJS({
      id: emptyUUID(),
      groupId: emptyUUID(),
      group: Group.fromJS({
        id: emptyUUID(),
        name: '',
        createdOn: new Date(),
        createdBy: window.application.user.id,
        feature: 'survey',
        archived: false,
        archivedOn: undefined,
        archivedBy: undefined,
        userIds: [],
        externalUsers: [],
        isPinned: false,
      } as IGroup),
      isAdvanceRegistration: true,
      surveyMappings: [],
      enableScanEntry: !this.canToggleEnableScanEntry,
      scanEntryEndDate: endDate,
      scanEntryStartDate: startDate,
      responsibleContactId: undefined,
      responsibleFallbackContactId: undefined,
      collectiveApprovalState: ApprovalState.NotRequired,
      isVisitorGroup: false,
      extraInfoJson: this.extraInfoJson,
      visitorGroupNames: [],
      userPresences: [],
      approvals: [],
      informationGroupMappings: [],
    } as IGroupSurveyExtension);

    if (this.needsApproval) {
      this.groupSurveyExtension.collectiveApprovalState = ApprovalState.Unknown;
      this.groupSurveyExtension.approvals.push(GroupSurveyExtensionApproval.fromJS({
        id: crypto.randomUUID(),
        groupSurveyExtensionId: emptyUUID(),
        approvalState: ApprovalState.Unknown,
        approvalGroupId: undefined as any, //field is required, but for the vSelect we want nothing selected in the beginning
        approvedByContactId: undefined,
        approvedAt: undefined,
      } as IGroupSurveyExtensionApproval));
    }
    // Reset selected survey groups state if applicable
    this.selectedSurveyGroups = [];
  }

  async saveGroup(group: GroupSurveyExtension): Promise<GroupSurveyExtension> {
    let res: GroupSurveyExtension;

    if (!group.id || group.id === emptyUUID()) {
      res = await this.api.create(group);
    } else {
      res = await this.api.update(group);
    }
    return res;
  }

  async onSave(): Promise<void> {
    if (this.needsApproval && !this.groupSurveyExtension?.approvals.length) {
      return;
    }

    this.addSurveysFromSelectedGroups();

    this.saving = true;
    try {
      const res = await (this.$refs.groupEditor as GroupEditor<GroupSurveyExtension>).onSave();
      if (res) { //res = undefined if saving failed
        this.groupSurveyExtension = res;

        //hide editor
        this.done = true;
      }
    } finally {
      this.saving = false;
    }
  }

  addSurveysFromSelectedGroups(): void {
    if (!this.groupSurveyExtension) {
      return;
    }

    const existingSurveyIds = new Set(this.groupSurveyExtension.surveyMappings.map(m => m.surveyId));
    const groups: GroupSurveyExtension[] = (this.$refs.groupSurveyGroupSelector as GroupSurveyGroupSelector)?.selectedGroups ?? [];
    const surveyIds = [...new Set(groups.flatMap(g => g.surveyMappings.map(m => m.surveyId)))]
        .filter(id => !existingSurveyIds.has(id));
    const mappings = surveyIds.map(id => GroupSurveyMapping.fromJS({
      id: emptyUUID(),
      groupSurveyExtensionId: emptyUUID(),
      surveyId: id,
    } as IGroupSurveyMapping));
    this.groupSurveyExtension.surveyMappings.push(...mappings);

    this.groupSurveyExtension.visitorGroupNames = groups.map(e => e.group.name);
  }

  async reloadSetting(): Promise<void> {
    try {
      const imageUrlSetting = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvancedRegistrationImageUrl');
      if (imageUrlSetting.result && typeof imageUrlSetting.result === 'string') {
        this.imageUrl = imageUrlSetting.result as string;
      }
    } catch (error) { //in case the setting does not exist
      console.error(error);
    }
    try {
      const allowToggleScanEntry = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvanceRegistrationOnlyAllowToggleForEnableScanEntry');
      this.canToggleEnableScanEntry = (allowToggleScanEntry.result! as string).toLocaleLowerCase() === 'true';
    } catch (error) { //in case the setting does not exist
      console.error(error);
    }
    try {
      const needsApproval = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvanceRegistrationOnlyNeedsApproval');
      this.needsApproval = (needsApproval.result! as string).toLocaleLowerCase() === 'true';
    } catch (error) { //in case the setting does not exist
      console.error(error);
    }
    try {
      const surveySelectionViaGroups = await this.settingsApi.getSystemSetting('VisitorManagementSettings.EnableAdvanceRegistrationSurveySelectionViaGroups');
      this.surveySelectionViaGroups = (surveySelectionViaGroups.result! as string).toLocaleLowerCase() === 'true';
      if (this.surveySelectionViaGroups) {
        this.$nextTick(() => (this.$refs.groupEditor as GroupEditor<GroupSurveyExtension>).tab = 'surveysFromGroup');
      }
    } catch (error) { //in case the setting does not exist
      console.error(error);
    }
    try {
      const extraInfoJson = await this.settingsApi.getSystemSetting('VisitorManagementSettings.AdvanceRegistrationExtraInfoJson');
      if (Array.isArray(extraInfoJson.result!)) {
        this.extraInfoJson = JSON.stringify(extraInfoJson.result);
      } else {
        this.extraInfoJson = '[]';
      }
    } catch (error) { //in case the setting does not exist
      console.error(error);
      this.extraInfoJson = '[]';
    }
  }

  formattedInstruction(): string {
    const key = 'visitorManagement.advanceRegistration.closingInstruction';
    if (!this.$te(key)) {
      return '';
    }

    const rawText = this.$t(key).toString();
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    return rawText.replace(
        urlRegex,
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="link-highlight">$1</a>',
    );
  }

  get canMultipleSelection(): boolean {
    return window.application.permissions.survey.group.multipleSelection;
  }

  resetForm(): void {
    this.done = false;
    this.initNewRegistration();
    
    this.$nextTick(() => {
      const groupEditor = this.$refs.groupEditor as GroupEditor<GroupSurveyExtension>;
      if (groupEditor && surveySelectionViaGroups ) {
        groupEditor.tab = 'surveysFromGroup';
      }
      else
      {
        groupEditor.tab = 'general';
      }
    });
  }
}
